/**
 * VMsDashboard — portada de Máquinas virtuales (/virt/vms).
 *
 * El listado completo sigue en /virt/vms/list. Aquí lo que no se ve en una
 * tabla: cuántas están encendidas, cuántas reportan por agente, cómo se reparten
 * por hipervisor y entorno, y el bloque de **vulnerabilidades** (`ciType="virt_vm"`).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CubeTransparentIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  BoltIcon,
  SignalIcon,
  CircleStackIcon,
  ServerStackIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import VulnerabilitiesPanel from './VulnerabilitiesPanel';
import { COLORS, pct, countBy, Tile, Donut, Legend, BarRow, Card, EmptyState } from './DashboardBits';

const POWER_LABEL = { on: 'vm.power.on', off: 'vm.power.off', suspended: 'vm.power.suspended' };
const POWER_COLOR = { on: COLORS.ok, off: COLORS.gray, suspended: COLORS.warn };

const AGENT_LABEL = {
  online: 'vm.agent.online',
  offline: 'vm.agent.offline',
  none: 'vm.agent.none',
  unknown: 'vm.agent.unknown',
};
const AGENT_COLOR = { online: COLORS.ok, offline: COLORS.warn, none: COLORS.gray, unknown: COLORS.muted };

const labelOf = (map, key, t) =>
  map[key] ? t(map[key]) : `${key}`.charAt(0).toUpperCase() + `${key}`.slice(1);

const groupLabels = (items) => items.map((item) => ({ ...item, label: item.key || 'Sin dato' }));

export default function VMsDashboard({ companyId }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [vms, setVms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await api.getVMs(companyId);
      setVms(Array.isArray(resp?.data) ? resp.data : []);
    } catch (err) {
      setError(err.message || t('vm.errorTitle'));
    } finally {
      setLoading(false);
    }
  }, [companyId, t]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const total = vms.length;
    const byPower = countBy(vms, (vm) => vm.power_state || 'off');
    const byAgent = countBy(vms, (vm) => vm.agent_status || 'none');
    const byHypervisor = countBy(vms, (vm) => vm.hypervisor || 'Sin hipervisor');
    const byEnvironment = countBy(vms, (vm) => vm.environment || 'Sin entorno');
    const online = vms.filter((vm) => vm.agent_status === 'online').length;
    const running = vms.filter((vm) => vm.power_state === 'on').length;
    const disk = vms.reduce((sum, vm) => sum + (Number(vm.disk_total_gb) || 0), 0);
    return { total, byPower, byAgent, byHypervisor, byEnvironment, online, running, disk };
  }, [vms]);

  // Los enlaces con parámetros (destacar, abrir, hypervisorId…) son del
  // listado clásico: se reenvían para no romper los enlaces internos.
  const deepQuery = searchParams.toString();
  if (deepQuery) return <Navigate to={`/virt/vms/list?${deepQuery}`} replace />;

  if (!companyId) {
    return (
      <div className="dbx-page">
        <EmptyState icon={CubeTransparentIcon} title={t('vm.selectCompany')} text={t('vm.selectCompanyText')} />
      </div>
    );
  }

  const powerSegments = groupLabels(stats.byPower).map((item) => ({
    key: item.key,
    label: labelOf(POWER_LABEL, item.key, t),
    value: item.value,
    color: POWER_COLOR[item.key] || COLORS.info,
  }));
  const agentSegments = stats.byAgent.map((item) => ({
    key: item.key,
    label: labelOf(AGENT_LABEL, item.key, t),
    value: item.value,
    color: AGENT_COLOR[item.key] || COLORS.gray,
  }));
  const agentCoverage = pct(stats.online, stats.total);
  const runningPct = pct(stats.running, stats.total);

  return (
    <div className="dbx-page">
      <header className="dbx-head">
        <div>
          <h1>{t('vm.title')}</h1>
          <p>{t('vm.subtitle')}</p>
        </div>
        <div className="dbx-head-actions">
          <button type="button" className="dbx-btn" onClick={load} disabled={loading}>
            <ArrowPathIcon />
            {t('vm.refresh')}
          </button>
          <Link to="/virt/vms/list" className="dbx-btn dbx-btn--primary">
            {t('vm.openList')}
            <ArrowRightIcon />
          </Link>
        </div>
      </header>

      {error && (
        <div className="dbx-state">
          <ExclamationTriangleIcon />
          <h2>{t('vm.errorTitle')}</h2>
          <p>{error}</p>
          <button type="button" className="dbx-btn" onClick={load}>{t('vm.retry')}</button>
        </div>
      )}

      {!error && loading && <div className="dbx-state"><ArrowPathIcon /><p>{t('vm.loading')}</p></div>}

      {!error && !loading && stats.total === 0 && (
        <EmptyState
          icon={CubeTransparentIcon}
          title={t('vm.empty.title')}
          text={t('vm.empty.text')}
          action={<Link to="/virt/vms/list" className="dbx-btn dbx-btn--primary">{t('vm.goList')}</Link>}
        />
      )}

      {!error && !loading && stats.total > 0 && (
        <>
          <div className="dbx-tiles">
            <Tile
              icon={CubeTransparentIcon}
              tone="info"
              label={t('vm.kpi.machines')}
              value={stats.total}
              hint={t('vm.kpi.hypervisors', { count: stats.byHypervisor.length })}
            />
            <Tile
              icon={BoltIcon}
              tone={runningPct > 0 ? 'ok' : 'warn'}
              label={t('vm.kpi.poweredOn')}
              value={stats.running}
              bar={runningPct}
              hint={t('vm.kpi.poweredOnPct', { pct: runningPct })}
            />
            <Tile
              icon={SignalIcon}
              tone={agentCoverage >= 80 ? 'ok' : 'warn'}
              label={t('vm.kpi.agent')}
              value={`${agentCoverage}%`}
              bar={agentCoverage}
              hint={t('vm.kpi.onOf', { online: stats.online, total: stats.total })}
            />
            <Tile
              icon={CircleStackIcon}
              tone="info"
              label={t('vm.kpi.disk')}
              value={`${(stats.disk / 1024).toFixed(1)} TB`}
              hint={t('vm.kpi.diskHint', { gb: stats.disk.toLocaleString('es-ES') })}
            />
          </div>

          <div className="dbx-grid">
            <Card icon={BoltIcon} title={t('vm.card.power')} subtitle={t('vm.card.powerSub')}>
              <div className="dbx-split">
                <Donut segments={powerSegments} center={stats.total} label={t('vm.kpi.machines').toLowerCase()} />
                <Legend items={powerSegments} total={stats.total} />
              </div>
            </Card>

            <Card icon={SignalIcon} tone="ok" title={t('vm.card.agent')} subtitle={t('vm.card.agentSub')}>
              <div className="dbx-card-body">
                {agentSegments.map((item) => (
                  <BarRow key={item.key} label={item.label} value={item.value} total={stats.total} color={item.color} />
                ))}
              </div>
            </Card>

            <Card icon={ServerStackIcon} title={t('vm.card.hypervisors')} subtitle={t('vm.card.hypervisorsSub')}>
              <div className="dbx-card-body">
                {stats.byHypervisor.slice(0, 6).map((item) => (
                  <BarRow key={item.key} label={item.key} value={item.value} total={stats.total} color={COLORS.info} />
                ))}
              </div>
            </Card>

            <Card icon={CubeTransparentIcon} title={t('vm.card.envs')} subtitle={t('vm.card.envsSub')}>
              <div className="dbx-card-body">
                {stats.byEnvironment.slice(0, 6).map((item) => (
                  <BarRow key={item.key} label={item.key} value={item.value} total={stats.total} color={COLORS.warn} />
                ))}
              </div>
            </Card>
          </div>

          <VulnerabilitiesPanel companyId={companyId} ciType="virt_vm" />
        </>
      )}
    </div>
  );
}
